/**
 * @file 文档页页面hooks
 * @author Wanzhongyun
 */
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import union from 'lodash/union';
import throttle from 'lodash/throttle';
import { useHistory, useLocation } from 'react-router-dom';

import innerText from '../../utils/inner-text';
import { IDocumentMenuItem } from './type';
import useDocFeedbackHooks from 'src/components/doc-feedback/docFeedbackHooks';
import useRateHooks from '../../components/document-rate/rateHooks';
import useBreadcrumbsListHooks from './components/doc-breadcrumb/docBreadcrumbHooks';
import { getDocumentHTML } from '../../api/pages/documentApi';
import hasClass from '../../utils/has-class';

// 遍历所有选项找到compare函数返回true的那个选项
// 遍历速度比较慢，使用需谨慎
const getSelectedItem = (
    theMenuList: IDocumentMenuItem[],
    compare: (item: IDocumentMenuItem, deep: number) => boolean,
    deep: number = 0
): IDocumentMenuItem | undefined => {
    let resMenuItem: IDocumentMenuItem | undefined;
    theMenuList.forEach(menu => {
        if (!resMenuItem) {
            if (compare(menu, deep)) {
                resMenuItem = menu;
                return;
            }

            if (menu.children.length > 0) {
                resMenuItem = getSelectedItem(menu.children, compare, deep + 1);
            }
        }
    });
    return resMenuItem;
};

// 遍历所有选项并将他们平铺成数组
const getMenuArray = (theMenuList: IDocumentMenuItem[]): IDocumentMenuItem[] => {
    let resMenuArray: IDocumentMenuItem[] = [];
    theMenuList.forEach(menuItem => {
        resMenuArray.push(menuItem);
        resMenuArray = resMenuArray.concat(getMenuArray(menuItem.children));
    });
    return resMenuArray;
};

const getElementById = (id: string): HTMLElement | undefined => {
    if (typeof document !== 'object') {
        return;
    }
    const theDom = document.getElementById(id) || undefined;
    theDom && theDom.parentNode && theDom.parentNode.removeChild(theDom);
    return theDom;
};

/**
 * 获取文档的 html hook
 */
const useDocumentDom = () => {
    const location = useLocation();
    const defaultVersionDom = useMemo(() => {
        const theDom = getElementById('doc_version');
        if (theDom) {
            const li = theDom.querySelector('li');
            if (li && li.getAttribute('th:each')) {
                return;
            }
        }
        return theDom;
    }, []);

    const defaultLangDom = useMemo(() => {
        const theDom = getElementById('doc_lang');
        if (theDom) {
            const li = theDom.querySelector('li');
            if (li && li.getAttribute('th:each')) {
                return;
            }
        }
        return theDom;
    }, []);

    const defaultDocumentDom = useMemo(() => {
        const theDom = getElementById('document_content');
        if (theDom) {
            if (theDom.getAttribute('th:utext') || theDom.getAttribute('th:text')) {
                return;
            }
        }
        return theDom;
    }, []);

    // didMount阶段不需要通过网络请求获取文档 html
    const isDidMountAndNotNeedLoadHtml = useRef<boolean>(!!(defaultVersionDom && defaultLangDom && defaultDocumentDom));

    const [versionDom, setVersionDom] = useState<HTMLElement | undefined>(defaultVersionDom);
    const [langDom, setLangDom] = useState<HTMLElement | undefined>(defaultLangDom);
    const [documentDom, setDocumentDom] = useState<HTMLElement | undefined>(defaultDocumentDom);

    useEffect(() => {
        if (isDidMountAndNotNeedLoadHtml.current) {
            return;
        }

        (async () => {
            let res;
            try {
                res = await getDocumentHTML(`${location.pathname}${location.search}`);
            } catch (err: any) {
                // ignore
            }
            if (res && res.text) {
                const htmlDom = document.createElement('html');
                htmlDom.innerHTML = res.text;

                // document使用的是同一个thymeleaf的模版，
                const titleDom = htmlDom.querySelector('title');
                if (titleDom) {
                    document.title = titleDom.innerHTML;
                }

                // :scope代表的是根元素
                const vDom = htmlDom.querySelector<HTMLElement>(':scope #doc_version') || undefined;
                vDom && setVersionDom(vDom);

                const lDom = htmlDom.querySelector<HTMLElement>(':scope #doc_lang') || undefined;
                lDom && setLangDom(lDom);

                const aDom = htmlDom.querySelector<HTMLElement>(':scope #document_content') || undefined;
                aDom && setDocumentDom(aDom);
            }
        })();
    }, [location.pathname, location.search, setVersionDom, setLangDom, setDocumentDom]);

    // didMount阶段不需要通过网络请求获取文档 html
    useEffect(() => {
        isDidMountAndNotNeedLoadHtml.current = false;
    }, []);

    return {
        versionDom,
        langDom,
        documentDom
    };
};

/**
 * 左侧、顶部tab、右侧锚点的 hook
 */
const useMenu = (documentDom?: HTMLElement) => {
    const [menuList, setMenuList] = useState<IDocumentMenuItem[]>([]);
    const [menuSelectedKeys, setMenuSelectedKeys] = useState<string[]>([]);
    const [leftMenuShow, setLeftMenuShow] = useState<boolean>(true);
    const [searchQuery, setSearchQuery] = useState<string>('');

    // 左侧菜单栏展开，关闭的状态
    const switchLeftMenuShow = useCallback(() => {
        setLeftMenuShow(oldValue => !oldValue);
    }, [setLeftMenuShow]);

    // 顶部 tab 菜单的内容，只需要一级菜单，故用children: []来覆盖
    const topMenuList: IDocumentMenuItem[] = useMemo(
        () =>
            menuList.map(item => ({
                ...item,
                children: []
            })),
        [menuList]
    );

    // 顶部 tab 菜单的选中项
    const topMenuSelectedKeys: string[] = useMemo(() => menuSelectedKeys.slice(0, 1), [menuSelectedKeys]);

    // 查看是否为paddlepaddl的api文档页面
    const isApiDocumentation: boolean = useMemo(
        () =>
            menuSelectedKeys[0] === '$0API 文档/documentation/docs/zh/api/index_cn.html' ||
            menuSelectedKeys[0] === '$0API 文档/documentation/docs/zh/develop/api/index_cn.html',
        [menuSelectedKeys]
    );

    // 页面转跳的话 需要清空搜索数据。
    useEffect(() => {
        if (!isApiDocumentation) {
            setSearchQuery('');
        }
    }, [isApiDocumentation]);

    // 左侧菜单的内容
    const subMenuList: IDocumentMenuItem[] = useMemo(() => {
        if (menuSelectedKeys.length === 0) {
            return [];
        }
        const subMenu = menuList.find(item => item.key === menuSelectedKeys[0]);
        if (subMenu) {
            return subMenu.children;
        }
        return [];
    }, [menuList, menuSelectedKeys]);

    // 本地检索过的menuList
    const filteredSubMenuList: IDocumentMenuItem[] = useMemo(() => {
        // 非 paddlepaddle api文档或者 空搜索状态下 返回原list
        if (!isApiDocumentation || searchQuery === '') {
            return subMenuList;
        }

        const filterSearch = (searchList: IDocumentMenuItem[]) => {
            return searchList.reduce((prev, curr) => {
                const filteredChildren: IDocumentMenuItem[] = curr.children.reduce((result, child) => {
                    const submenu = filterSearch(child.children);

                    return submenu.length > 0
                        ? [...result, { ...child, children: submenu }]
                        : child.title.toLowerCase().includes(searchQuery.toLowerCase())
                        ? [...result, child]
                        : result;
                }, [] as IDocumentMenuItem[]);

                return [
                    ...prev,
                    ...(filteredChildren.length > 0 || curr.title.toLowerCase().includes(searchQuery.toLowerCase())
                        ? [{ ...curr, children: filteredChildren.length > 0 ? filteredChildren : curr.children }]
                        : [])
                ];
            }, [] as IDocumentMenuItem[]);
        };

        return filterSearch(subMenuList);
    }, [isApiDocumentation, searchQuery, subMenuList]);

    // 左侧菜单的选中项
    const subMenuSelectedKeys: string[] = useMemo(() => menuSelectedKeys.slice(1), [menuSelectedKeys]);

    // subMenuOpenKeys与subMenuSelectedKeys有什么区别
    const [subMenuOpenKeys, _setSubMenuOpenKeys] = useState<string[]>([]);
    const setSubMenuOpenKeys = useCallback<typeof _setSubMenuOpenKeys>(
        value => {
            _setSubMenuOpenKeys(oldValue => {
                const theValue = typeof value === 'function' ? value(oldValue) : value;
                if (oldValue.length !== theValue.length) {
                    return theValue;
                }
                const oldSet = new Set(oldValue);
                if (theValue.every(i => oldSet.has(i))) {
                    return oldValue;
                }
                return theValue;
            });
        },
        [_setSubMenuOpenKeys]
    );

    useEffect(() => {
        if (!documentDom) {
            return;
        }

        // 递归遍历 menu 树，抽取出 menu item 数据。
        // 同时把选中的 menuItem 添加到 selectedKeys 里
        const getMenuItemFromDom = (
            theMenuDom: NodeListOf<Element>,
            deep: number = 0
        ): [IDocumentMenuItem[], string[]] => {
            if (!theMenuDom) {
                return [[], []];
            }
            const selectedKeys: string[] = [];
            const theMenuList: IDocumentMenuItem[] = [];
            theMenuDom.forEach(tree1Dom => {
                const titleAItem: HTMLAnchorElement | null = tree1Dom.querySelector(':scope > a');
                const title = innerText(titleAItem);
                const href = titleAItem?.getAttribute('href') === '#' ? '' : titleAItem?.href ?? '';
                const link = href.replace(window.location.origin, '').replace(/#$/, '');
                const key = `$${deep}${title}${link}`;
                if (hasClass(tree1Dom, 'current')) {
                    selectedKeys.push(key);
                }

                const [child, childSelectedKeys] = getMenuItemFromDom(
                    tree1Dom.querySelectorAll(':scope > ul > li'),
                    deep + 1
                );

                const tree1Obj: IDocumentMenuItem = {
                    key,
                    title,
                    link,
                    children: child
                };

                // 如果 link 是空，则寻找子菜单中标题为 Overview 的链接
                if (!tree1Obj.link) {
                    const childOverviewItem = child.find(i => i.title === 'Overview');
                    if (childOverviewItem) {
                        tree1Obj.link = childOverviewItem.link;
                    }
                }

                theMenuList.push(tree1Obj);
                childSelectedKeys.forEach(s => {
                    selectedKeys.push(s);
                });
            });
            return [theMenuList, selectedKeys];
        };
        const [newMenuList, selectedKeys] = getMenuItemFromDom(documentDom.querySelectorAll('.wy-menu > ul > li'));
        setMenuSelectedKeys(selectedKeys);

        setSubMenuOpenKeys(oldKeys => union(oldKeys, selectedKeys.slice(1)));
        setMenuList(newMenuList);
    }, [documentDom, setSubMenuOpenKeys]);

    // 右侧锚点的内容
    const [anchorMenuList, setAnchorMenuList] = useState<IDocumentMenuItem[]>([]);
    const [anchorSelectedKeys, setAnchorSelectedKeys] = useState<string[]>([]);
    const [anchorOpenKeys, setAnchorOpenKeys] = useState<string[]>([]);
    useEffect(() => {
        if (!documentDom) {
            return;
        }

        const allKeys: string[] = [];
        // 递归遍历 menu 树，抽取出 menu item 数据。
        // 同时把选中的 menuItem 添加到 selectedKeys 里
        const getMenuItemFromDom = (theMenuDomList: NodeListOf<Element>, deep: number = 0): IDocumentMenuItem[] => {
            if (theMenuDomList.length === 0) {
                return [];
            }
            const theMenuList: IDocumentMenuItem[] = [];
            theMenuDomList.forEach(tree1Dom => {
                const titleAItem: HTMLAnchorElement | null = tree1Dom.querySelector(':scope > a');
                const title = innerText(titleAItem);
                const href = titleAItem && titleAItem.href ? titleAItem.href : '';
                const link = href.replace(window.location.origin, '');
                const key = `$${deep}${title}${link}`;
                const child = getMenuItemFromDom(tree1Dom.querySelectorAll(':scope > ul > li'), deep + 1);
                allKeys.push(key);
                const tree1Obj: IDocumentMenuItem = {
                    key,
                    title,
                    link,
                    children: child
                };

                theMenuList.push(tree1Obj);
            });
            return theMenuList;
        };

        const newAnchorMenuList = getMenuItemFromDom(
            documentDom.querySelectorAll('#navigation-toc.toc > ul > li > ul > li')
        );
        setAnchorMenuList(newAnchorMenuList);
        setAnchorOpenKeys(allKeys);
    }, [documentDom]);

    // 右侧锚点的索引，方便快速查找对应的锚点数据
    const anchorMenuMapByLink = useMemo(() => {
        const theMap = new Map<string, IDocumentMenuItem>();
        const anchorMenuArray = getMenuArray(anchorMenuList);
        anchorMenuArray.forEach(menuItem => {
            const key = (menuItem.link || '').replace(/[^#]*#/, '');
            theMap.set(key, menuItem);
        });
        return theMap;
    }, [anchorMenuList]);

    const getAnchorMenuItemByLink = useCallback(
        (key: string) => {
            return anchorMenuMapByLink.get(key);
        },
        [anchorMenuMapByLink]
    );

    return {
        menuList,
        leftMenuShow,
        topMenuList,
        subMenuList,
        filteredSubMenuList,
        anchorMenuList,
        menuSelectedKeys,
        topMenuSelectedKeys,
        subMenuSelectedKeys,
        anchorSelectedKeys,
        subMenuOpenKeys,
        anchorOpenKeys,
        isApiDocumentation,
        searchQuery,

        setSearchQuery,
        setMenuList,
        switchLeftMenuShow,
        setMenuSelectedKeys,
        setSubMenuOpenKeys,
        setAnchorSelectedKeys,

        getAnchorMenuItemByLink
    };
};

/**
 * 版本列表 hook
 */
const useVersionList = (versionDom?: HTMLElement) => {
    const [versionList, setVersionList] = useState<IDocumentMenuItem[]>([]);
    const [versionSelectedKeys, setVersionSelectedKeys] = useState<string>('');

    useEffect(() => {
        if (!versionDom) {
            return;
        }
        const newVersionSelectedKeys: string[] = [];
        const versionItemDoms: HTMLLIElement[] = Array.from(versionDom.querySelectorAll(':scope > li'));
        const versionItems: IDocumentMenuItem[] = versionItemDoms.map((item, index) => {
            let title = innerText(item);
            let link = '';

            const aDom: HTMLAnchorElement | null = item.querySelector(':scope > a');
            if (aDom) {
                title = innerText(aDom);
                const href = aDom.href || '';
                link = href.replace(window.location.origin, '');
            }
            const key = `$${index}${title}`;
            if (hasClass(item, 'current')) {
                newVersionSelectedKeys.push(key);
            }
            return {
                key,
                title,
                link,
                children: []
            };
        });
        setVersionList(versionItems);
        setVersionSelectedKeys(newVersionSelectedKeys[0]);
    }, [versionDom]);

    const theSetVersionSelectedKeys = useCallback(
        (key: string) => {
            const targetItem = getSelectedItem(versionList, item => item.key === key);
            // console.log('targetItem:', targetItem);
            if (targetItem && targetItem.link) {
                // 这里有可能是2.4,也有可能是master
                if (targetItem.title === 'v2.4') {
                    window.open('/inference/v2.4/guides/introduction/index_intro.html', '_self');
                    return;
                }
                if (targetItem.title === 'v2.5') {
                    window.open('/inference/v2.5/guides/introduction/index_intro.html', '_self');
                    return;
                }
                if (targetItem.title === 'v2.6') {
                    window.open('/inference/v2.6/guides/introduction/index_intro.html', '_self');
                    return;
                }
                if (targetItem.title === 'v3.0') {
                    window.open('/inference/v3.0/guides/introduction/index_intro.html', '_self');
                    return;
                }
                if (targetItem.title === 'master') {
                    window.open('/inference/master/guides/introduction/index_intro.html', '_self');
                    return;
                }
                if (window.location.pathname.includes('inference') && targetItem.link.includes('change-version')) {
                    window.location.href = '/inference/' + targetItem.title + '/product_introduction/summary.html';
                    // product_introduction/inference_intro.html
                } else {
                    window.location.href = targetItem.link;
                }
                console.log('targetLink:', targetItem);
            }
            return setVersionSelectedKeys(key);
        },
        [versionList, setVersionSelectedKeys]
    );

    return {
        versionList,
        versionSelectedKeys,
        setVersionList,
        setVersionSelectedKeys: theSetVersionSelectedKeys
    };
};

/**
 * 语言列表 hook
 */
const useLangList = (langDom?: HTMLElement) => {
    const [langList, setLangList] = useState<IDocumentMenuItem[]>([]);
    const [langSelectedKeys, setLangSelectedKeys] = useState<string>('');

    useEffect(() => {
        if (!langDom) {
            return;
        }
        const newLangSelectedKeys: string[] = [];
        const langItemDoms: HTMLLIElement[] = Array.from(langDom.querySelectorAll(':scope > li'));
        const langItems: IDocumentMenuItem[] = langItemDoms.map((item, index) => {
            let title = innerText(item);
            let link = '';

            const aDom: HTMLAnchorElement | null = item.querySelector(':scope > a');
            if (aDom) {
                title = innerText(aDom);
                const href = aDom.href || '';
                link = href.replace(window.location.origin, '');
            }

            const key = `$${index}${title}`;
            if (hasClass(item, 'current')) {
                newLangSelectedKeys.push(key);
            }
            return {
                key,
                title,
                link,
                children: []
            };
        });
        setLangSelectedKeys(newLangSelectedKeys[0]);
        setLangList(langItems);
    }, [langDom]);

    const theSetLangSelectedKeys = useCallback(
        (key: string) => {
            const targetItem = getSelectedItem(langList, item => item.key === key);
            if (targetItem && targetItem.link) {
                window.location.href = targetItem.link;
            }
            return setLangSelectedKeys(key);
        },
        [langList, setLangSelectedKeys]
    );

    return {
        langList,
        langSelectedKeys,
        setLangSelectedKeys: theSetLangSelectedKeys
    };
};

/**
 * 文档正文 hook
 */
const useDocumentArticle = (documentDom?: HTMLElement) => {
    const [documentArticle, setDocumentArticle] = useState<HTMLElement>();

    useEffect(() => {
        if (!documentDom) {
            return;
        }
        const textDom: HTMLElement | null = documentDom.querySelector('[role="main"].document');
        if (!textDom) {
            return;
        }
        const anchorDomList = textDom.querySelectorAll(':scope *[id]');
        anchorDomList.forEach(anchorDom => {
            const newAnchor = document.createElement('a');
            newAnchor.className = 'hide-anchor';
            newAnchor.name = anchorDom.id;
            newAnchor.id = anchorDom.id;
            if (anchorDom.childNodes[0]) {
                anchorDom.insertBefore(newAnchor, anchorDom.childNodes[0]);
            } else {
                anchorDom.appendChild(newAnchor);
            }
            anchorDom.removeAttribute('id');
        });

        textDom.querySelectorAll<HTMLElement>(':scope .highlight').forEach(codeDom => {
            codeDom.className = `${codeDom.className} hljs`;
        });

        // 添加别名相关dom
        // 事件在组件里绑定，hook里只生成dom
        textDom.querySelectorAll<HTMLDivElement>(':scope .paddle_doc_api_alias_tags').forEach(aliasDom => {
            const labelDom = document.createElement('div');
            labelDom.className = 'paddle-doc-alias-tags-label';
            const iconDom = document.createElement('span');
            iconDom.className = 'paddle-doc-alias-tags-label-icon';
            labelDom.appendChild(iconDom);
            labelDom.appendChild(document.createTextNode('查看属性与别名'));
            aliasDom.parentElement?.insertBefore(labelDom, aliasDom);
        });

        setDocumentArticle(textDom);
    }, [documentDom]);

    return documentArticle;
};

/**
 * 页面hooks入口
 */
const useDocumentStore = () => {
    const history = useHistory();
    const location = useLocation();
    const { versionDom, langDom, documentDom } = useDocumentDom();

    const menuHook = useMenu(documentDom);
    const versionListHook = useVersionList(versionDom);
    const langListHook = useLangList(langDom);
    const breadcrumbsListHook = useBreadcrumbsListHooks(documentDom);
    const documentArticle = useDocumentArticle(documentDom);
    const rateHook = useRateHooks();
    const docFeedbackHook = useDocFeedbackHooks();

    const isNavLink: boolean = ['/guides/index_cn.html', 'guides/01_paddle2.0_introduction/index_cn.html'].some(
        item => location.pathname.includes(item) && location.pathname.includes('/zh/')
    );

    // 根据 hash 跳转到锚点位置
    const scrollToAnchor = useCallback(() => {
        const anchorName = location.hash.slice(1);
        if (!anchorName) {
            return;
        }
        const anchorDom = document.getElementById(anchorName) || document.querySelector(`a[name="${anchorName}"]`);
        if (anchorDom) {
            anchorDom.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    }, [location.hash]);
    useEffect(scrollToAnchor, [scrollToAnchor, location.hash, documentDom]);

    useEffect(() => {
        if (history.action === 'PUSH') {
            window.scrollTo({ top: 0 });
        }
    }, [history, documentDom]);

    // 寻找所有的锚点并计算位置，找到当前正在显示的锚点并高亮对应的标签
    const { anchorMenuList, setAnchorSelectedKeys, getAnchorMenuItemByLink } = menuHook;
    // 缓存需要监听的锚点 dom 列表
    const archorDomList = useMemo(() => {
        if (!documentDom || anchorMenuList.length === 0) {
            return [];
        }
        // 获取文档正文中所有带id属性和a[name]属性的节点，这些节点有可能被锚点锚定
        const anchoDomListNotSort = Array.from(
            new Set([
                ...Array.from(documentDom.querySelectorAll<HTMLElement>(':scope [id]')),
                ...Array.from(documentDom.querySelectorAll<HTMLElement>(':scope a[name]'))
            ])
        );
        // 排序
        const anchoDomListSort = anchoDomListNotSort.map(anchoDom => ({
            dom: anchoDom,
            top: anchoDom.getBoundingClientRect().top
        }));
        anchoDomListSort.sort((a, b) => a.top - b.top);
        const anchoDomList = anchoDomListSort.map(item => item.dom);

        // 再获取锚点链接的列表
        const anchorMenuNameList = getMenuArray(anchorMenuList)
            .map(menuItem => menuItem.link)
            .map(link => (link ? link.replace(/[^#]*#/, '') : ''))
            .filter(link => link);
        // 取 dom 和锚点链接的交集，这些 dom 作为最后要监听滚动事件时计算位置的 dom
        const anchorMenuSet = new Set(anchorMenuNameList);
        return anchoDomList.filter(domItem => {
            const anchorDomName = domItem.id || domItem.getAttribute('name') || '';
            return anchorMenuSet.has(anchorDomName);
        });
    }, [documentDom, anchorMenuList]);

    // 监听锚点dom的位置
    useEffect(() => {
        // 使用 throttle 和 requestAnimationFrame 减轻计算压力
        const scrollHandle = throttle(
            () =>
                requestAnimationFrame(() => {
                    if (archorDomList.length === 0) {
                        return;
                    }
                    let targetArchorDom: HTMLElement | undefined;
                    // top 位置在屏幕中线和顶部之间的锚点 dom 被认为是正在展示的锚点
                    const screenCenterLine = window.innerHeight / 3;
                    const offset = 10;

                    // 获取锚点 dom 的 top 值
                    const archorDomTopList = archorDomList.map(
                        archorDom => archorDom.getBoundingClientRect().top + offset
                    );
                    // 找到最接近屏幕顶端的锚点
                    const archorDomBellowTop = archorDomTopList.filter(top => top > 0);
                    if (archorDomBellowTop.length > 0) {
                        const targetTop = archorDomBellowTop[0];
                        const targetIndex = archorDomTopList.indexOf(targetTop);
                        // 如果这个锚点在屏幕上半部分，就认为它是当前展示的锚点
                        if (targetTop < screenCenterLine) {
                            targetArchorDom = archorDomList[targetIndex];
                        }
                        // 如果这个锚点在屏幕下半部分则认为他之前的那个锚点是当前正在展示的
                        else {
                            targetArchorDom = archorDomList[targetIndex - 1] || archorDomList[targetIndex];
                        }
                    }
                    // 屏幕里没有正在展示的锚点，直接取列表末尾的
                    else {
                        targetArchorDom = archorDomList.slice(-1)[0];
                    }

                    if (targetArchorDom) {
                        const targetLink = targetArchorDom.getAttribute('name') || '';
                        const targetMenuItem = getAnchorMenuItemByLink(targetLink);
                        if (targetMenuItem) {
                            setAnchorSelectedKeys([targetMenuItem.key]);
                        }
                    }
                }),
            500
        );
        window.addEventListener('scroll', scrollHandle);
        window.addEventListener('resize', scrollHandle);
        return () => {
            window.removeEventListener('scroll', scrollHandle);
            window.removeEventListener('resize', scrollHandle);
        };
    }, [documentDom, anchorMenuList, archorDomList, setAnchorSelectedKeys, getAnchorMenuItemByLink]);

    return {
        menuHook,
        versionListHook,
        langListHook,
        breadcrumbsListHook,
        documentArticle,
        rateHook,
        docFeedbackHook,
        isNavLink,
        scrollToAnchor
    };
};

export default useDocumentStore;
